import { noop } from 'lodash-es';
import { Maybe } from '@iss/libs/graphql';
import { FormsModule, NgControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Component,
  ContentChild,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Self,
  TemplateRef,
} from '@angular/core';
import { NgFor, NgTemplateOutlet } from '@angular/common';
import { FocusMonitor } from '@angular/cdk/a11y';

import { ControlValueAccessor } from '../forms';

import { DisabledDirective } from '@iss/core/utils';

let uniqueId = 0;

@Component({
  selector: 'iss-exclusive-buttons[ngModel],iss-exclusive-buttons[formControlName],iss-exclusive-buttons[formControl]',
  templateUrl: './exclusive-buttons.component.html',
  styleUrls: [ './exclusive-buttons.component.scss' ],
  standalone: true,
  imports: [
    DisabledDirective,
    FormsModule,
    NgFor,
    NgTemplateOutlet,
  ],
})
export class ExclusiveButtonsComponent<T, K = T> implements ControlValueAccessor<K>, OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  public onChange: (value: K) => unknown = noop;
  public onTouched: () => unknown = noop;
  public disabled = false;
  public focused = false;
  public name = `iss-exclusive-buttons-${uniqueId++}`;
  public value!: K;

  @Input({ required: true }) public items!: readonly T[];
  @Input() public identityFn: (item: T) => K = (item) => item as unknown as K;
  @Input() public textFn: (item: T) => string = (item) => `${ String(item) }`;
  @Input() public readonly: Maybe<boolean>;

  @ContentChild(TemplateRef) public templateRef?: TemplateRef<T>;

  public constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly focusMonitor: FocusMonitor,
    @Self() public readonly ngControl: NgControl,
  ) {
    this.ngControl.valueAccessor = this;
  }

  public ngOnInit(): void {
    this.focusMonitor
      .monitor(this.elementRef, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((origin) => this.onFocusChange(!!origin));
  }

  public ngOnDestroy(): void {
    this.focusMonitor.stopMonitoring(this.elementRef);
  }

  public registerOnChange(fn: (value: K) => unknown): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => unknown): void {
    this.onTouched = fn;
  }

  public setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  public writeValue(value: K): void {
    this.value = value;
  }

  private onFocusChange(focused: boolean): void {
    this.focused = focused;
    if (!focused) {
      this.onTouched();
    }
  }
}
